import React from "react";
import { Skeleton } from "@mui/material";

const range = (count) => Array.from(Array(count));

const CountCardSkeleton = () => (
  <div className="submission_preview__card">
    <div className="submission_preview__card__content flex-fill justify-content-between">
      <Skeleton animation="wave" variant="text" width="60%" height={18} />
      <Skeleton animation="wave" variant="text" width="90%" height={12} />
      <Skeleton
        animation="wave"
        variant="text"
        width="50%"
        height={12}
        style={{ marginBottom: 8 }}
      />
      <Skeleton animation="wave" variant="text" width="40%" height={24} />
    </div>
  </div>
);

const MetricRowSkeleton = () => (
  <div className="flex flex-column">
    <Skeleton animation="wave" variant="text" width="35%" height={16} />
    <Skeleton animation="wave" variant="text" width="70%" height={12} />
    <div className="flex flex-column gap--8">
      <Skeleton animation="wave" variant="rounded" width="100%" height={8} />
      <div className="flex justify-space-between">
        <Skeleton animation="wave" variant="text" width={24} height={12} />
        <Skeleton animation="wave" variant="text" width={32} height={12} />
      </div>
    </div>
  </div>
);

const ListRowSkeleton = () => (
  <div className="flex justify-space-between align-center">
    <Skeleton animation="wave" variant="text" width="50%" height={12} />
    <Skeleton animation="wave" variant="text" width="20%" height={12} />
  </div>
);

const IssuesCardSkeleton = () => (
  <div className="submission_preview__card">
    <div className="submission_preview__card__content submission_preview__card__content--no-btm flex-grow-0">
      <div className="submission_preview__title-container">
        <Skeleton animation="wave" variant="text" width={120} height={16} />
        <Skeleton animation="wave" variant="text" width={200} height={12} />
      </div>
      <Skeleton animation="wave" variant="text" width={32} height={24} />
    </div>
    <div className="submission_preview__scroll-table flex-fill mt--16">
      <table className="summary_table summary_table--square summary_table--no-border">
        <thead>
          <tr>
            <th>
              <Skeleton animation="wave" variant="text" width="60%" height={12} />
            </th>
            <th>
              <Skeleton animation="wave" variant="text" width="60%" height={12} />
            </th>
            <th>
              <Skeleton animation="wave" variant="text" width="60%" height={12} />
            </th>
          </tr>
        </thead>
        <tbody className="sm">
          {range(3).map((_, index) => (
            <tr key={index}>
              <td>
                <Skeleton animation="wave" variant="rounded" width={100} height={18} />
              </td>
              <td>
                <Skeleton animation="wave" variant="text" width={24} height={12} />
              </td>
              <td>
                <Skeleton animation="wave" variant="text" width={40} height={12} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const EntitySummarySkeleton = () => (
  <div className="entity_summary">
    <div className="entity_summary__header">
      <div className="entity_summary__header__title">
        <Skeleton animation="wave" variant="text" width={140} height={20} />
        <Skeleton animation="wave" variant="text" width={100} height={12} />
      </div>
      <div className="entity_summary__header__numbers">
        <Skeleton animation="wave" variant="text" width={80} height={16} />
        <Skeleton animation="wave" variant="text" width={110} height={12} />
      </div>
    </div>
    <div className="entity_summary__stats" style={{ "--column-count": 5 }}>
      {range(5).map((_, index) => (
        <div className="flex flex-column" key={index}>
          <Skeleton animation="wave" variant="text" width="70%" height={12} />
          <Skeleton animation="wave" variant="text" width="40%" height={20} />
        </div>
      ))}
    </div>
    <div className="entity_summary__table-container">
      <table className="summary_table">
        <thead>
          <tr>
            {range(5).map((_, index) => (
              <th key={index}>
                <Skeleton animation="wave" variant="text" width="60%" height={12} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {range(3).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {range(5).map((_, cellIndex) => (
                <td key={cellIndex}>
                  <Skeleton animation="wave" variant="text" width="80%" height={12} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SubmissionReceiptSkeleton = () => {
  return (
    <div className="submission_preview submission_preview--receipt">
      <div className="submission_preview__header">
        <div className="submission_preview__header__details">
          <Skeleton animation="wave" variant="text" width={120} height={14} />
          <Skeleton animation="wave" variant="text" width={240} height={24} />
          <Skeleton animation="wave" variant="text" width={180} height={14} />
        </div>
        <div className="submission_preview__header__user">
          <Skeleton animation="wave" variant="circular" width={40} height={40} />
          <div className="submission_preview__user__text">
            <Skeleton animation="wave" variant="text" width={120} height={12} />
            <Skeleton animation="wave" variant="text" width={160} height={12} />
          </div>
        </div>
      </div>

      <div className="submission_preview__divider"></div>

      <div className="submission_preview__content">
        {range(4).map((_, index) => (
          <CountCardSkeleton key={index} />
        ))}
      </div>

      <div className="submission_preview__divider"></div>

      <div className="submission_preview__content">
        <div className="submission_preview__card">
          <div className="submission_preview__card__content">
            <Skeleton animation="wave" variant="text" width={80} height={14} />
          </div>
          <div className="submission_preview__divider submission_preview__divider--no-mg"></div>
          <div className="submission_preview__card__content">
            <div className="flex flex-column gap--16">
              {range(3).map((_, index) => (
                <MetricRowSkeleton key={index} />
              ))}
            </div>
          </div>
        </div>
        <div className="submission_preview__card">
          <div className="submission_preview__card__content">
            <Skeleton animation="wave" variant="text" width={220} height={14} />
          </div>
          <div className="submission_preview__divider submission_preview__divider--no-mg"></div>
          <div className="submission_preview__card__content flex-fill">
            <div className="flex justify-space-between align-center">
              <Skeleton animation="wave" variant="text" width={120} height={14} />
              <Skeleton animation="wave" variant="text" width={24} height={12} />
            </div>
            <div className="flex flex-column gap--4 mt--8 submission_preview__scroll-list">
              {range(4).map((_, index) => (
                <ListRowSkeleton key={index} />
              ))}
            </div>
          </div>
          <div className="submission_preview__divider submission_preview__divider--no-mg"></div>
          <div className="submission_preview__card__content flex-fill">
            <div className="flex justify-space-between align-center">
              <Skeleton animation="wave" variant="text" width={120} height={14} />
              <Skeleton animation="wave" variant="text" width={24} height={12} />
            </div>
            <div className="mt--8 submission_preview__scroll-list submission_preview__two-col">
              {range(6).map((_, index) => (
                <Skeleton key={index} animation="wave" variant="text" width="70%" height={12} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="submission_preview__divider"></div>

      <div className="submission_preview__content">
        <IssuesCardSkeleton />
        <IssuesCardSkeleton />
      </div>

      <div className="submission_preview__divider"></div>

      <EntitySummarySkeleton />
    </div>
  );
};

export default SubmissionReceiptSkeleton;
